import { Constants } from './constants';
import { format, isValid, parseISO, addDays, subDays, differenceInDays, differenceInMonths, differenceInYears, getYear, getDate, getMonth, formatISO } from 'date-fns';

export class DateHelper
{

    public static getFormattedDate(date: Date | string, fallback: string = "-"): string
    {
        let parsedDate: Date;

        if (!date)
        {
            return fallback;
        }

        if (typeof date === 'string')
        {
            parsedDate = parseISO(date);
        } else
        {
            parsedDate = date;
        }

        if (DateHelper.dateIsValid(parsedDate) && parsedDate.getFullYear() > 1900)
        {
            return this.getDateTimeAsFormat(parsedDate, Constants.DateFormatShort);
        }
        return fallback;
    }
    public static getFormattedDateTime(date: Date | string, fallback: string = "-"): string
    {
        let parsedDate: Date;

        if (!date)
        {
            return fallback;
        }

        if (typeof date === 'string')
        {
            parsedDate = parseISO(date);
        }
        else
        {
            parsedDate = date;
        }

        if (DateHelper.dateIsValid(parsedDate) && parsedDate.getFullYear() >= 1900)
        {
            return this.getDateTimeAsFormat(parsedDate, Constants.DateTimeFormatShort);
        }
        return fallback;
    }

    public static dateIsValid(value: Date | string): boolean
    {
        return this.dateTimeIsValid(value);
    }

    public static dateTimeIsValid(value: Date | string): boolean
    {
        let date: Date;

        if (typeof value === 'string')
        {
            date = parseISO(value);
        } else
        {
            date = value;
        }

        return isValid(date);
    }

    public static dateTimeIsSet(value: Date | string): boolean
    {
        let date: Date;

        if (typeof value === 'string')
        {
            date = parseISO(value);
        } else
        {
            date = value;
        }

        if (isValid(date) && date.getFullYear() > 1900)
        {
            return true;
        }
        return false;
    }

    public static getDateTimeAsFormat(date: Date, formatStr: string)
    {
        if (isValid(date) && date.getFullYear() >= 1900)
        {
            const dateFormatted = format(date, formatStr);
            // console.log("Formatted date: ", dateFormatted);
            return dateFormatted;
        }
        console.error("Invalid date: ", date);
        return null;
    }

    public static addDays(days: number, formatStr: string): string
    {
        return format(addDays(new Date(), days), formatStr);
    }

    public static subtractDays(days: number, formatStr: string): string
    {
        return format(subDays(new Date(), days), formatStr);
    }

    public static formatForDateTimeInput(isoDate: string): string
    {
        if (!isoDate) return null;
        const localDate = parseISO(isoDate);
        return format(localDate, "yyyy-MM-dd'T'HH:mm:ss");
    }

    public static toISOString(value: Date | string): string
    {
        const date = typeof value === 'string' ? parseISO(value) : value;
        if (isValid(date) && getYear(date) > 1900 && (getDate(date) > 1 || getMonth(date) > 0))
        {
            return formatISO(date);
        }
        return null;
    }

    public static toUTCFormat(localDate: string): string
    {
        const date = parseISO(localDate);
        const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
        return formatISO(utcDate);
    }

    public static compareDates(value1: Date | string, value2: Date | string): number
    {
        const date1 = typeof value1 === 'string' ? parseISO(value1) : value1;
        const date2 = typeof value2 === 'string' ? parseISO(value2) : value2;
        return date1.getTime() - date2.getTime();
    }

    public static convertToUTC(localDate: string): string
    {
        const date = parseISO(localDate);
        // const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
        return formatISO(date);
    }

    public static isValidYear(value: Date | string): boolean
    {
        const date = typeof value === 'string' ? parseISO(value) : value;
        return isValid(date) && getYear(date) > 1900;
    }

    public static getDaysSinceDate(value: Date | string): number
    {
        const previousDate = typeof value === 'string' ? parseISO(value) : value;
        return differenceInDays(new Date(), previousDate);
    }

    public static getDaysDiff(valueA: Date | string, valueB: Date | string): number
    {
        const dateA = typeof valueA === 'string' ? parseISO(valueA) : valueA;
        const dateB = typeof valueB === 'string' ? parseISO(valueB) : valueB;
        return differenceInDays(dateA, dateB);
    }

    public static getDaysAndSuffixSinceDate(value: Date | string, captionSuffix = " ago"): [daysSinceLastActivity: number, daysSinceLastActivitySuffix: string]
    {
        const previousDate = typeof value === 'string' ? parseISO(value) : value;
        const now = new Date();

        let daysSinceLastActivity = differenceInDays(now, previousDate);
        let daysSinceLastActivitySuffix = (daysSinceLastActivity == 1) ? ` day${captionSuffix}` : ` days${captionSuffix}`;

        if (daysSinceLastActivity > 31)
        {
            daysSinceLastActivity = differenceInMonths(now, previousDate);
            daysSinceLastActivitySuffix = (daysSinceLastActivity == 1) ? ` month${captionSuffix}` : ` months${captionSuffix}`;

            if (daysSinceLastActivity > 12)
            {
                daysSinceLastActivity = differenceInYears(now, previousDate);
                daysSinceLastActivitySuffix = (daysSinceLastActivity == 1) ? ` year${captionSuffix}` : ` years${captionSuffix}`;
            }
        }

        return [daysSinceLastActivity, daysSinceLastActivitySuffix];
    }

    public static getDaysSinceDateCaption(value: Date | string): string
    {
        if (!value) return "";

        const previousDate = typeof value === 'string' ? parseISO(value) : value;
        const [daysSinceLastActivity, daysSinceLastActivitySuffix] = this.getDaysAndSuffixSinceDate(previousDate);
        if (daysSinceLastActivity > 0)
        {
            return daysSinceLastActivity + daysSinceLastActivitySuffix;
        }
        return "";
    }
}
